import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Globe, ArrowRight, CheckCircle2, AlertTriangle, Key } from 'lucide-react';
import { useTheme } from '../ThemeContext';

export const ApiNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();
  const method = data.method || 'GET';
  const url = data.url || '';
  const mappings = data.mappings || [];

  const methodBadge = {
    GET: isDark ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' : 'bg-blue-50 text-blue-700 border-blue-200',
    POST: isDark ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
    PUT: isDark ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-amber-50 text-amber-700 border-amber-200',
    DELETE: isDark ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-rose-50 text-rose-700 border-rose-200',
  }[method] || 'bg-slate-500/20 text-slate-300';

  return (
    <div className={`w-80 rounded-xl border transition-all ${
      isDark ? 'bg-slate-900 border-slate-800 shadow-xl' : 'bg-white border-slate-200 shadow-lg'
    } ${
      selected ? 'border-violet-500 ring-2 ring-violet-500/30' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`!h-3.5 !w-3.5 !border-2 !bg-violet-500 hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />

      {/* Header */}
      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-violet-950/80 to-purple-900/40 border-violet-500/20'
          : 'bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-700'
          }`}>
            <Globe className="h-3.5 w-3.5" />
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? 'text-violet-200' : 'text-violet-900'
          }`}>
            {data.label || 'External API / Webhook'}
          </span>
        </div>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold border ${methodBadge}`}>
          {method}
        </span>
      </div>

      {/* Body Details */}
      <div className="p-3.5 space-y-3">
        {/* URL Box */}
        <div className={`p-2 rounded-lg border font-mono text-[11px] truncate ${
          data.url
            ? (isDark ? 'bg-slate-950/60 border-slate-800 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700')
            : (isDark ? 'bg-slate-950/40 border-dashed border-slate-800 text-slate-500 italic' : 'bg-slate-50 border-dashed border-slate-300 text-slate-400 italic')
        }`} title={data.url || 'Set API Endpoint URL...'}>
          {data.url || 'Set API Endpoint URL...'}
        </div>

        {/* Auth Badge if configured */}
        {((data.authType && data.authType !== 'none') || data.authToken) && (
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono border ${
            isDark ? 'bg-violet-950/30 border-violet-800/40 text-violet-300' : 'bg-violet-50 border-violet-200 text-violet-700'
          }`}>
            <Key className="h-3 w-3 text-violet-500" />
            <span>
              {data.authType === 'apiKey' 
                ? `API Key (${data.apiKeyName || 'X-API-Key'})` 
                : (data.authType === 'basic' ? 'Basic Auth' : 'Bearer Token')}
            </span>
          </div>
        )}

        {/* Variables Saved preview */}
        {mappings.length > 0 && (
          <div className="space-y-1">
            <div className={`text-[10px] uppercase font-bold tracking-wider ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}>
              Extract to Variables:
            </div>
            <div className="flex flex-wrap gap-1">
              {mappings.map((m, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono border ${
                    isDark
                      ? 'bg-violet-950/40 border-violet-800/50 text-violet-300'
                      : 'bg-violet-50 border-violet-200 text-violet-800'
                  }`}
                >
                  <span>{m.path}</span>
                  <ArrowRight className="h-2.5 w-2.5 opacity-60" />
                  <span className="font-bold">{`{{${m.variable}}}`}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 2 Output Handles: Success & Failed */}
        <div className={`space-y-2 pt-2 border-t text-xs ${
          isDark ? 'border-slate-800' : 'border-slate-100'
        }`}>
          {/* Success Output */}
          <div className="flex items-center justify-between relative py-0.5">
            <span className="flex items-center gap-1.5 text-emerald-500 text-[11px] font-semibold">
              <CheckCircle2 className="h-3 w-3" />
              <span>Success (2xx)</span>
            </span>
            <Handle
              type="source"
              position={Position.Right}
              id="success"
              className={`!h-3 !w-3 !border-2 !bg-emerald-500 hover:!scale-125 transition-transform ${
                isDark ? '!border-slate-900' : '!border-white'
              }`}
            />
          </div>

          {/* Failed Output */}
          <div className="flex items-center justify-between relative py-0.5">
            <span className="flex items-center gap-1.5 text-rose-500 text-[11px] font-semibold">
              <AlertTriangle className="h-3 w-3" />
              <span>Failed / Error</span>
            </span>
            <Handle
              type="source"
              position={Position.Right}
              id="fail"
              className={`!h-3 !w-3 !border-2 !bg-rose-500 hover:!scale-125 transition-transform ${
                isDark ? '!border-slate-900' : '!border-white'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
